import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Input, Button } from '@/components/ui';
import { useSystemSettings } from '../hooks/useSystemSettings';

const infraSchema = z.object({
  maintenanceMode: z.boolean(),
  maintenanceMessage: z.string().min(5, "Thông báo quá ngắn"),
  logLevel: z.enum(['debug', 'info', 'warning', 'error']),
  cacheTTL: z.number().min(0),
  enableAnalytics: z.boolean(),
});

type InfraFormValues = z.infer<typeof infraSchema>;

interface InfraSectionProps {
  onChange?: (isDirty: boolean) => void;
  onSaveSuccess?: () => void;
}

export function InfraSection({ onChange, onSaveSuccess }: InfraSectionProps) {
  const { infra, updateInfra } = useSystemSettings();

  const { register, handleSubmit, reset, formState: { errors, isDirty } } = useForm<InfraFormValues>({
    resolver: zodResolver(infraSchema),
    defaultValues: infra || {}
  });

  // Sync data when loaded
  React.useEffect(() => {
    if (infra) {
      reset(infra);
    }
  }, [infra, reset]);

  React.useEffect(() => {
    if (onChange) {
      onChange(isDirty);
    }
  }, [isDirty, onChange]);

  const onSubmit = async (data: InfraFormValues) => {
    try {
      await updateInfra(data);
      if (onSaveSuccess) onSaveSuccess();
    } catch (err) {
      // Error handled by mutation
    }
  };

  return (
    <form id="infra-form" onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      {/* Maintenance Mode */}
      <div className={`p-6 rounded-2xl border transition-all ${infra?.maintenanceMode ? 'bg-red-50 border-red-100' : 'bg-slate-50 border-slate-100'}`}>
        <div className="flex items-center justify-between mb-6">
          <div className="space-y-1">
            <h4 className={`text-sm font-bold ${infra?.maintenanceMode ? 'text-red-900' : 'text-slate-900'}`}>Chế độ bảo trì hệ thống</h4>
            <p className="text-xs text-slate-500">Khi bật, chỉ Super Admin mới có quyền truy cập vào hệ thống.</p>
          </div>
          <input
            type="checkbox"
            {...register('maintenanceMode')}
            className="w-12 h-6 rounded-full appearance-none bg-slate-300 checked:bg-red-600 relative cursor-pointer transition-colors before:content-[''] before:absolute before:w-4 before:h-4 before:bg-white before:rounded-full before:top-1 before:left-1 checked:before:left-7 before:transition-all shadow-inner"
          />
        </div>

        <div className="space-y-2">
          <label className="text-xs font-bold text-slate-500  tracking-wider">Thông báo bảo trì</label>
          <textarea
            {...register('maintenanceMessage')}
            rows={3}
            className="w-full bg-white rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-600/20 transition-all"
            placeholder="Nhập thông báo sẽ hiển thị cho người dùng..."
          />
          {errors.maintenanceMessage && <p className="text-[10px] text-red-500 font-bold">{errors.maintenanceMessage.message}</p>}
        </div>
      </div>

      {/* Technical Config */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="text-sm font-bold text-slate-700">Mức độ ghi Log (System Logs)</label>
          <select
            {...register('logLevel')}
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-600/20 transition-all"
          >
            <option value="debug">Debug (Chi tiết nhất)</option>
            <option value="info">Info (Thông thường)</option>
            <option value="warning">Warning (Cảnh báo)</option>
            <option value="error">Error (Chỉ lỗi hệ thống)</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-bold text-slate-700">Thời gian lưu Cache (Phút)</label>
          <Input
            {...register('cacheTTL', { valueAsNumber: true })}
            type="number"
          />
        </div>
      </div>

      {/* Other Flags */}
      <div className="bg-slate-50/50 p-6 rounded-2xl border border-slate-100 flex items-center justify-between">
        <div className="space-y-1">
          <p className="text-sm font-bold text-slate-900">Thu thập dữ liệu phân tích (Analytics)</p>
          <p className="text-xs text-slate-400">Cho phép hệ thống thu thập dữ liệu sử dụng để cải thiện trải nghiệm.</p>
        </div>
        <input type="checkbox" {...register('enableAnalytics')} className="w-5 h-5 rounded border-slate-300 text-brand-600 focus:ring-brand-600" />
      </div>
    </form>
  );
}
